import React, { useEffect, useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { BoldText, DemiLightText, MediumText } from '../../lib/Text';
import { usePayrollAuthContext } from '../../context/PayrollAuthContext';
import { updateLockedSectionsAPI } from '../../api/auth';
import { colors } from '../../lib/colors';
import SvgIcon from '../SvgIcon';
import { useQueryClient } from '@tanstack/react-query';
import { markTabLockConfigured } from '../../lib/tabLockPromotion';
import {
    LOCKABLE_SECTION_TREE,
    LockableChildSection,
    LockableSection,
    changedLockDecisions,
    getSectionLockState,
    isDecisionLockSection,
    isScheduleActionSection,
    toStorageLockDecisions,
    lockSupportOfResponse,
    normalizeLockedSections,
    toServerLockedSections,
} from '../../lib/lockedSections';

const getStatusStyle = (state: 'locked' | 'partial' | 'unlocked') => {
    if (state === 'locked') {
        return { label: '전체 잠금', backgroundColor: '#EEF4FF', color: colors.primary };
    }
    if (state === 'partial') {
        return { label: '일부 잠금', backgroundColor: '#FFF7E8', color: '#D97706' };
    }
    return { label: '열림', backgroundColor: '#F3F5F7', color: '#8E9AA0' };
};

const Toggle = ({
    isOn,
    disabled,
    label,
    onClick,
}: {
    isOn: boolean;
    disabled?: boolean;
    /** 어느 탭의 잠금인지 — 스위치만 보고도 알 수 있어야 한다 */
    label: string;
    onClick: () => void;
}) => (
    <div
        role="switch"
        aria-checked={isOn}
        aria-label={`${label} 잠금`}
        aria-disabled={disabled}
        onClick={() => !disabled && onClick()}
        style={{
            width: 48,
            height: 28,
            borderRadius: 14,
            backgroundColor: isOn ? colors.primary : '#E0E4E8',
            position: 'relative',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.25s ease',
            flexShrink: 0,
            opacity: disabled ? 0.6 : 1,
        }}
    >
        <div
            style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: 'white',
                position: 'absolute',
                top: 3,
                left: isOn ? 23 : 3,
                transition: 'left 0.25s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }}
        />
    </div>
);

const TabLockSettings: React.FC<{ isModal?: boolean }> = ({ isModal = false }) => {
    const {
        lockedSections,
        setLockedSections,
        applyClientLockDecisions,
        getLockSupport,
        reportLockFetch,
        isSectionLocked,
    } = usePayrollAuthContext();
    const [saving, setSaving] = useState(false);
    /** 저장 실패를 화면에서 말한다 — 잠금 설정이 조용히 안 먹히면 안 된다 */
    const [saveError, setSaveError] = useState<string | null>(null);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    const authObject = getItemWithExpiration('authObject');
    const authorization = getItemWithExpiration('authorization');
    const queryClient = useQueryClient();
    /*
     * 잠금 계약 판정 전(unknown)에는 손실 시그널·기프티콘 토글을 막는다.
     * 화면의 두 값은 아직 서버 것이 아니라 이 브라우저의 기본값·옛 결정이다 — 그걸 보고 누른 값을
     * 기록하면, v3 가 확인된 뒤 이관으로 서버의 「명시적 결정」이 되어 버린다.
     */
    const lockSupport = getLockSupport();
    const decisionPending = lockSupport === 'unknown';
    /*
     * 스케줄 동작 세 토글(수업 생성·수정·삭제)은 서버가 새 계약(v3)일 때만 저장할 수 있다.
     * 구서버(legacy)는 이 잠금을 모르고 브라우저에도 기록하지 않으므로, 눌러도 아무것도 남지 않는다 —
     * 조용히 되돌아가는 토글을 두지 않고 막아 둔다. 판정 전(unknown)에는 스케줄 부모 토글도 막는다.
     */
    const scheduleActionHint =
        lockSupport === 'v3'
            ? null
            : lockSupport === 'unknown'
              ? '설정을 불러오는 중이에요'
              : lockSupport === 'error'
                ? '설정을 불러오지 못했어요'
                : '지금 서버에서는 바꿀 수 없어요';
    const isToggleBlocked = (key: string) =>
        (isDecisionLockSection(key) && !isScheduleActionSection(key) && decisionPending) ||
        (key === 'schedule' && decisionPending) ||
        (isScheduleActionSection(key) && lockSupport !== 'v3');

    useEffect(() => {
        const partialKeys = LOCKABLE_SECTION_TREE.filter(
            (section) => getSectionLockState(lockedSections, section) === 'partial',
        ).map((section) => section.key);

        if (partialKeys.length === 0) return;

        setExpandedKeys((prev) => {
            const nextKeys = [...prev];
            partialKeys.forEach((key) => {
                if (!nextKeys.includes(key)) nextKeys.push(key);
            });
            return nextKeys;
        });
    }, [lockedSections]);

    /**
     * 잠금 설정 저장.
     *
     * 서버는 자기 enum 밖의 키가 하나라도 섞이면 **요청 전체를 400 으로 거절**한다.
     * 그래서 서버가 아는 키만 골라 보내고(toServerLockedSections),
     * 서버가 모르는 섹션(기프티콘·데일리 브리핑)은 이 브라우저에 따로 기록한다.
     * 예전에는 둘을 함께 보내다가 저장이 통째로 실패했고, 화면은 아무 말도 하지 않았다.
     */
    /**
     * 저장에 성공한 잠금 값을 이 계정의 잠금 조회 캐시에도 덮어쓴다.
     *
     * 재조회(invalidate)는 늦게 올 수 있다. 그 사이 조회 훅(usePayrollAuth)을 쓰는 화면
     * (알림센터·Gnb 등)이 새로 뜨면 **저장 전 캐시**를 다시 Provider 에 반영해, 방금 잠근 기프티콘이
     * 풀린다. 그래서 invalidate 전에 캐시를 저장 결과와 맞춘다.
     *  · 캐시는 axios 응답 형태(`{ status, data: {...} }`)다. 잠금 필드만 덮고 isSettingPayrolPassword 등은 둔다.
     *  · 캐시가 없으면 만들지 않는다(다음 조회가 채운다 — 비밀번호 설정 여부를 지어내지 않기 위해).
     *  · `onlyIfLegacyCache`: 구 계약 저장 응답(두 키 없음)을 v3 캐시에 덮으면 서버가 정한 결정이
     *    배열에서 빠져 풀린 것으로 보인다 — 그래서 구 계약 저장은 캐시도 구 계약일 때만 맞춘다.
     */
    const syncLockQueryCache = (body: any, options: { onlyIfLegacyCache?: boolean } = {}) => {
        const queryKey = ['getCheckPayrollAuthQuery', authObject?.adminId ?? null];
        /*
         * 건너뛸지는 **먼저 읽어서** 정한다. setQueryData 의 updater 가 old 를 그대로 돌려줘도
         * React Query 는 성공 갱신으로 처리해 status·dataUpdatedAt 을 바꾼다(undefined 일 때만 건너뜀).
         * 그러면 조회 훅이 그 옛 캐시를 「새 응답」으로 Provider 에 다시 넘긴다 — 조회 실패로 legacy 에
         * 내려와 있던 상태가 옛 v3 로 되살아나, 방금 브라우저에 기록한 잠금이 지워지고 풀린다.
         */
        const old: any = queryClient.getQueryData(queryKey);
        if (!old || !old.data || typeof old.data !== 'object') return;
        if (options.onlyIfLegacyCache && lockSupportOfResponse(old.data) === 'v3') return;
        const next = { ...old.data, lockedSections: body.lockedSections };
        if (!options.onlyIfLegacyCache) {
            next.lockDecisions = body.lockDecisions;
            next.lockVersion = body.lockVersion;
        }
        queryClient.setQueryData(queryKey, { ...old, data: next });
    };

    const persistLockedSections = async (sections: string[]) => {
        const normalizedSections = normalizeLockedSections(sections);
        const serverSections = toServerLockedSections(normalizedSections);
        setSaving(true);
        setSaveError(null);
        /*
         * 서버가 손실 시그널·기프티콘 결정을 아는가(v3) — 지원 상태는 잠금 조회 응답이 정한다.
         *  · v3          : 배열(두 키 제외) + 이번 토글로 **바뀐** 결정 키만 lockDecisions 로 보낸다.
         *  · legacy/unknown : 지금처럼 두 키는 이 브라우저에 기록한다.
         */
        const isV3 = getLockSupport() === 'v3';
        if (isV3) {
            const accountId = authObject?.adminId ? String(authObject.adminId) : null;
            // 섹션 키로 계산하고 **보내기 직전에** 저장 키로 바꾼다(섹션 키를 보내면 서버가 400)
            const lockDecisions = toStorageLockDecisions(changedLockDecisions(lockedSections, normalizedSections));
            try {
                const res = await updateLockedSectionsAPI(authObject?.adminId, serverSections, authorization, {
                    contract: true,
                    lockDecisions,
                });
                if (res?.status === 200 && Array.isArray(res.data?.lockedSections)) {
                    // 응답으로 지원 상태를 다시 판정한다(구서버가 헤더를 무시했으면 legacy 로 내려간다)
                    reportLockFetch(accountId, { kind: 'data', body: res.data });
                    syncLockQueryCache(res.data);
                    markTabLockConfigured();
                    queryClient.invalidateQueries(['getCheckPayrollAuthQuery']);
                } else {
                    if (res?.status === 400) {
                        // 서버가 새 계약을 모른다(롤백 등) — 아무것도 저장되지 않았다. 다시 조회해 판정을 맞춘다
                        queryClient.invalidateQueries(['getCheckPayrollAuthQuery']);
                    }
                    setSaveError('탭 잠금 설정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
                }
            } catch (err) {
                console.error('탭 잠금 설정 실패:', err);
                setSaveError('탭 잠금 설정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
            } finally {
                setSaving(false);
            }
            return;
        }
        /*
         * 서버가 받지 않는 키는 왕복 없이 바로 반영한다 (저장 실패해도 남아야 한다).
         * 단, 판정 전(unknown)에는 기록하지 않는다 — 두 토글은 막혀 있고, 다른 토글을 저장하면서
         * 화면의 두 값(서버 것이 아님)을 브라우저 결정으로 굳히면 안 된다.
         */
        if (!decisionPending) applyClientLockDecisions(normalizedSections);
        try {
            const res = await updateLockedSectionsAPI(authObject?.adminId, serverSections, authorization);
            if (res?.status === 200) {
                setLockedSections(res.data?.lockedSections || serverSections);
                if (Array.isArray(res.data?.lockedSections)) {
                    syncLockQueryCache(res.data, { onlyIfLegacyCache: true });
                }
                markTabLockConfigured();
                queryClient.invalidateQueries(['getCheckPayrollAuthQuery']);
            } else {
                setSaveError('탭 잠금 설정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
            }
        } catch (err) {
            console.error('탭 잠금 설정 실패:', err);
            setSaveError('탭 잠금 설정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            setSaving(false);
        }
    };

    const toggleExpanded = (sectionKey: string) => {
        setExpandedKeys((prev) =>
            prev.includes(sectionKey) ? prev.filter((key) => key !== sectionKey) : [...prev, sectionKey],
        );
    };

    const handleParentToggle = async (section: LockableSection) => {
        // 막힌 토글은 저장하지 않는다 (스위치가 막혀 있지만 진입점에서도 한 번 더)
        if (isToggleBlocked(section.key)) return;
        const childKeys = section.children?.map((child) => child.key) ?? [];
        const isParentLocked = lockedSections.includes(section.key);
        const nextSections = isParentLocked
            ? lockedSections.filter((key) => key !== section.key && !childKeys.includes(key))
            : [...lockedSections.filter((key) => !childKeys.includes(key)), section.key];

        await persistLockedSections(nextSections);
    };

    const handleChildToggle = async (section: LockableSection, child: LockableChildSection) => {
        if (isToggleBlocked(child.key)) return;
        const childKeys = section.children?.map((item) => item.key) ?? [];
        const isParentLocked = lockedSections.includes(section.key);

        if (isParentLocked) {
            const unlockedChildKey = child.key;
            const siblingKeysToLock = childKeys.filter((key) => key !== unlockedChildKey);
            const baseSections = lockedSections.filter((key) => key !== section.key && !childKeys.includes(key));
            await persistLockedSections([...baseSections, ...siblingKeysToLock]);
            return;
        }

        const isChildLocked = lockedSections.includes(child.key);
        const nextSections = isChildLocked
            ? lockedSections.filter((key) => key !== child.key)
            : [...lockedSections, child.key];

        await persistLockedSections(nextSections);
    };

    return (
        <Col
            style={{
                backgroundColor: 'white',
                borderRadius: isModal ? 0 : 15,
                padding: isModal ? '16px 0' : '28px 32px',
                boxShadow: isModal ? 'none' : '0px 2px 10px rgba(0, 0, 0, 0.06)',
            }}
        >
            {!isModal && (
                <>
                    <Row style={{ alignItems: 'center', marginBottom: 8 }}>
                        <SvgIcon name="SvgLock" size={18} fill={colors.Text_1} stroke={colors.Text_1} style={{ marginRight: 8 }} />
                        <BoldText fontSize={18} lineHeight={26} color="#3D4244">
                            탭 잠금 설정
                        </BoldText>
                    </Row>
                    <DemiLightText fontSize={14} lineHeight={20} color="#8E9AA0" className="mb-[24px]">
                        2차 비밀번호를 입력해야 접근할 수 있는 탭을 선택하세요
                    </DemiLightText>
                </>
            )}

            {isModal && (
                <DemiLightText fontSize={14} lineHeight={20} color="#8E9AA0" className="mb-[16px]">
                    2차 비밀번호를 입력해야 접근할 수 있는 탭을 선택하세요
                </DemiLightText>
            )}

            {saveError && (
                <div
                    role="alert"
                    style={{
                        marginBottom: 12,
                        borderRadius: 9,
                        border: '1px solid #FECACA',
                        background: '#FEF2F2',
                        color: '#B91C1C',
                        fontSize: 13,
                        lineHeight: '19px',
                        padding: '9px 12px',
                    }}
                >
                    {saveError}
                </div>
            )}

            <Col style={{ gap: 0 }}>
                {LOCKABLE_SECTION_TREE.map((tab, index) => {
                    const lockState = getSectionLockState(lockedSections, tab);
                    const status = getStatusStyle(lockState);
                    const isLocked = lockState === 'locked';
                    const children = tab.children ?? [];
                    const hasChildren = children.length > 0;
                    const togglePending = isToggleBlocked(tab.key);
                    const isExpanded = expandedKeys.includes(tab.key);
                    const lockedChildCount =
                        lockState === 'locked'
                            ? children.length
                            : children.filter((child) => lockedSections.includes(child.key)).length;

                    return (
                        <Col
                            key={tab.key}
                            style={{
                                borderBottom: index < LOCKABLE_SECTION_TREE.length - 1 ? '1px solid #F2F4F6' : 'none',
                            }}
                        >
                            <Row
                                style={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '16px 0',
                                }}
                            >
                                <Col style={{ minWidth: 0 }}>
                                    <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <MediumText fontSize={15} lineHeight={22} color="#3D4244">
                                            {tab.label}
                                        </MediumText>
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                height: 22,
                                                padding: '0 8px',
                                                borderRadius: 11,
                                                backgroundColor: status.backgroundColor,
                                                color: status.color,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                lineHeight: '22px',
                                            }}
                                        >
                                            {status.label}
                                        </span>
                                        {hasChildren && lockState === 'partial' && (
                                            <span
                                                style={{
                                                    color: '#8E9AA0',
                                                    fontSize: 12,
                                                    lineHeight: '18px',
                                                }}
                                            >
                                                {lockedChildCount}/{children.length}
                                            </span>
                                        )}
                                    </Row>
                                    <DemiLightText fontSize={13} lineHeight={18} color="#B0BCC8">
                                        {tab.description}
                                    </DemiLightText>
                                    {togglePending && (
                                        <DemiLightText fontSize={12} lineHeight={18} color="#8E9AA0">
                                            설정을 불러오는 중이에요
                                        </DemiLightText>
                                    )}
                                </Col>

                                <Row style={{ alignItems: 'center', gap: 12, flexShrink: 0 }}>
                                    {hasChildren && (
                                        <button
                                            type="button"
                                            onClick={() => toggleExpanded(tab.key)}
                                            style={{
                                                height: 30,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                border: '1px solid #E2E8F0',
                                                borderRadius: 8,
                                                background: '#FFFFFF',
                                                padding: '0 10px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <DemiLightText fontSize={13} lineHeight={18} color="#6B7684">
                                                상세 설정
                                            </DemiLightText>
                                            <SvgIcon
                                                name={isExpanded ? 'SvgUpStrokeArrow' : 'SvgDownStrokeArrow'}
                                                size={12}
                                                stroke="#8E9AA0"
                                                fill="none"
                                            />
                                        </button>
                                    )}
                                    <Toggle
                                        isOn={isLocked}
                                        disabled={saving || togglePending}
                                        label={tab.label}
                                        onClick={() => handleParentToggle(tab)}
                                    />
                                </Row>
                            </Row>

                            {hasChildren && isExpanded && (
                                <Col
                                    style={{
                                        gap: 0,
                                        padding: '2px 0 14px 12px',
                                        borderLeft: '2px solid #EEF2F6',
                                        marginBottom: 2,
                                    }}
                                >
                                    {children.map((child) => {
                                        /* 스케줄 동작 키는 지원 상태별 판정(구서버=열림)을 그대로 보여 준다 */
                                        const isChildLocked = isScheduleActionSection(child.key)
                                            ? isSectionLocked(child.key)
                                            : isLocked || lockedSections.includes(child.key);

                                        return (
                                            <Row
                                                key={child.key}
                                                style={{
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    minHeight: 42,
                                                    padding: '7px 0 7px 12px',
                                                }}
                                            >
                                                <Col style={{ minWidth: 0 }}>
                                                    <MediumText fontSize={14} lineHeight={20} color="#4F5965">
                                                        {child.label}
                                                    </MediumText>
                                                    {child.description && (
                                                        <DemiLightText fontSize={12} lineHeight={18} color="#B0BCC8">
                                                            {child.description}
                                                        </DemiLightText>
                                                    )}
                                                    {isToggleBlocked(child.key) && scheduleActionHint && (
                                                        <DemiLightText fontSize={12} lineHeight={18} color="#8E9AA0">
                                                            {scheduleActionHint}
                                                        </DemiLightText>
                                                    )}
                                                </Col>
                                                <Toggle
                                                    isOn={isChildLocked}
                                                    disabled={saving || isToggleBlocked(child.key)}
                                                    label={child.label}
                                                    onClick={() => handleChildToggle(tab, child)}
                                                />
                                            </Row>
                                        );
                                    })}
                                </Col>
                            )}
                        </Col>
                    );
                })}
            </Col>
        </Col>
    );
};

export default TabLockSettings;
